/**
 * The "Névtábla" — the sign a driver holds up in arrivals.
 *
 * Full-screen overlay rather than a route of its own, so dismissing it puts the
 * driver straight back on the trip they were reading. It keeps the screen awake
 * (Wake Lock where available, silently ignored elsewhere) and asks for landscape
 * because that is how a phone is held up over a barrier.
 *
 * Branded vs plain is the trip's decision (`ph_branding_allowed`), not ours:
 * a white-label partner's guest must not be met by a Pickup Hungary sign.
 *
 * Focus, Escape and the scroll lock are PortalModal's (@restart/ui).
 */
import React, { useEffect } from 'react';
import useWakeLock from './useWakeLock';
import PortalModal from './Components/PortalModal';
import { CloseIcon } from './Components/Icons';
import BrandLogo from './Components/BrandLogo';

export default function NameSign({
  name, meta = '', branded, onClose,
}) {
  useWakeLock(true);

  useEffect(() => {
    // Best-effort: only allowed in fullscreen on most browsers, so a rejection
    // is expected and must stay silent. The layout works either way.
    const orientation = typeof window !== 'undefined' && window.screen
      ? window.screen.orientation
      : null;
    if (orientation && typeof orientation.lock === 'function') {
      Promise.resolve(orientation.lock('landscape')).catch(() => {});
    }
    return () => {
      if (orientation && typeof orientation.unlock === 'function') {
        try {
          orientation.unlock();
        } catch {
          // Nothing to unlock.
        }
      }
    };
  }, []);

  return (
    <PortalModal
      className={`pp-sign ${branded ? 'pp-sign--branded' : 'pp-sign--plain'}`}
      label="Névtábla"
      onHide={onClose}
    >
      <button type="button" className="pp-sign__close" aria-label="Bezárás" onClick={onClose}>
        <CloseIcon size={20} />
      </button>

      {branded ? (
        <div className="pp-sign__brand">
          <BrandLogo variant="white" height={72} />
        </div>
      ) : null}

      <div className="pp-sign__name">{name}</div>

      {branded ? <div className="pp-sign__rule" /> : null}
      {branded && meta ? <div className="pp-sign__meta">{meta}</div> : null}

      <div className="pp-sign__hint">Fordítsd fekvő helyzetbe · a képernyő ébren marad</div>
    </PortalModal>
  );
}
